import {
  getConfigureOptions,
  type ConfigureFamily,
  type ConfigureOptionsResponse,
} from "@/api/configure-options";
import type { RegionCode } from "@/api/filter";
import {
  applyAutoSelect,
  perMachinePartNos,
} from "@/utils/configure-auto-select";
import {
  catalogItemsForFamily,
  normUpper,
} from "@/utils/configure-catalog-display";
import { configuredOverrideTotal } from "@/utils/configure-override-sum";
import {
  applyUserSelectionOverrides,
  itemsToSelectionMap,
  loadProductSelection,
  overridesToSelectionMap,
} from "@/utils/configure-storage";
import { configureSelectablePartNos } from "@/utils/configure-selection-preview";
import { parseDecimalString } from "@/utils/cost-display";
import { type MdsFilterState } from "@/utils/mds-configure";
import {
  createStableRequestKey,
  isRequestAbortError,
  SharedRequestCache,
  toCalculationRateLimitError,
} from "@/utils/calculation-request-control";

const configureOptionsRequestCache =
  new SharedRequestCache<ConfigureOptionsResponse>({
    ttlMs: 5_000,
    maxEntries: 128,
  });

export function clearConfiguredInvestmentRequestCache(): void {
  configureOptionsRequestCache.clear();
}

export function invalidateConfiguredInvestmentRequestCache(): void {
  configureOptionsRequestCache.clearFulfilled();
}

/** MDS product family key (A230 / A240 / …) from the product id/name. */
function mdsProductKey(productId: string): string {
  const m = String(productId)
    .toUpperCase()
    .match(/A\d{3}/);
  return m ? m[0] : "";
}

/**
 * POC `computeConfiguredMachinePriceForApps` — the comparison-card
 * `machinePriceOverride`.
 *
 * POC parity: when called for the comparison grid
 * the POC sets `configState._appsOverride = apps`, which forces
 * `useSavedSelection = false`, so it IGNORES whatever the user manually toggled
 * in the configure popup and re-derives a FRESH AUTO-SELECTED configuration from
 * the application fasteners + region. This function is the unchanged pure-auto
 * baseline; `computeConfiguredCardOverride` layers the CR-006 user delta on it.
 *
 * The cost-engine card path deliberately ignores `loadProductSelection`.
 * The display-only recommended total, however, seeds from the same persisted
 * draft as Configure Solution before re-applying family-controlled defaults.
 * That keeps optional selected rows (for example maintenance contracts) in the
 * footer without changing the cost-engine override.
 */
type ConfiguredInvestmentArgs = {
  productId: string;
  region: RegionCode;
  applicationIds: string[];
  mspAutoFeedEnabled: boolean;
  requiredMachines: number;
  signal?: AbortSignal;
};

type ConfigureOptionsLoader = (
  costPath: boolean,
) => Promise<ConfigureOptionsResponse>;

/**
 * Make at most one configure-options request for each catalog view during one
 * calculation. The legacy cost-path and true regional catalogs are distinct,
 * while both sides of the CR-006 manual delta share the regional response.
 */
function configureOptionsLoader(
  args: ConfiguredInvestmentArgs,
): ConfigureOptionsLoader {
  const byCostPath = new Map<boolean, Promise<ConfigureOptionsResponse>>();
  return (costPath) => {
    const pending = byCostPath.get(costPath);
    if (pending) return pending;
    const query = {
      region: args.region,
      applicationIds: [...args.applicationIds].sort(),
      mspAutoFeedEnabled: args.mspAutoFeedEnabled,
      costPath,
    };
    const key = createStableRequestKey("configure-options", {
      productId: args.productId,
      ...query,
    });
    const request = configureOptionsRequestCache.getOrCreate(
      key,
      (signal) => getConfigureOptions(args.productId, query, signal),
      args.signal,
    );
    byCostPath.set(costPath, request);
    return request;
  };
}

export async function computeConfiguredMachinePriceOverride(
  args: ConfiguredInvestmentArgs,
): Promise<string | undefined> {
  return computeAutoSelectedTotal(args, "card", {
    loadResponse: configureOptionsLoader(args),
    costPath: true,
  });
}

/**
 * CR-006 (Option 1 — delta): comparison-card override for a product the user
 * has saved a Configure Solution selection for. It is the auto card baseline
 * (machine + auto options, NO tooling — POC-exact) PLUS the user's NET change,
 * priced WITH tooling from the true regional/modal catalog:
 *
 *   configuredCard = autoNoTooling + (userWithTooling − autoWithTooling)
 *                  = machine + userOptions + (userTooling − autoTooling)
 *
 * So opening Configure with no changes leaves the card at the auto price (delta
 * = 0 — the ever-present auto tooling cancels), while adding a per-fastener
 * tooling item (A-041-… ) OR a catalog option moves the card by exactly that
 * item. The pure-auto baseline deliberately keeps the legacy NA→EU cost-path
 * catalog for POC parity; the delta must not use it because doing so can filter
 * valid NA-only user selections. Un-configured machines keep the pure auto card.
 */
export async function computeConfiguredCardOverride(
  args: ConfiguredInvestmentArgs,
): Promise<string | undefined> {
  const loadResponse = configureOptionsLoader(args);
  const autoNoTooling = await computeAutoSelectedTotal(args, "card", {
    loadResponse,
    costPath: true,
  });
  // undefined = the auto card sends no override (e.g. MDS A230 → server-resolved);
  // keep that behaviour so configuring an A230 does not fabricate a card price.
  if (autoNoTooling === undefined) return undefined;
  const autoWithTooling = await computeAutoSelectedTotal(args, "card", {
    includeTooling: true,
    loadResponse,
    costPath: false,
  });
  const userWithTooling = await computeAutoSelectedTotal(args, "configured", {
    includeTooling: true,
    loadResponse,
    costPath: false,
  });
  const base = parseDecimalString(autoNoTooling) ?? 0;
  const autoT = parseDecimalString(autoWithTooling ?? "0") ?? 0;
  const userT = parseDecimalString(userWithTooling ?? "0") ?? 0;
  const total = base + (userT - autoT);
  return total > 0 ? total.toFixed(4) : undefined;
}

/**
 * Total shown when Configure Solution opens. Unlike the cost-card override,
 * this applies explicit user selection/quantity overrides after the current
 * recommendation, applies assignment-scoped quantities to untouched rows and
 * uses normal modal pricing.
 */
export async function computeRecommendedConfigurationTotal(
  args: ConfiguredInvestmentArgs,
): Promise<string | undefined> {
  return computeAutoSelectedTotal(args, "recommended", {
    loadResponse: configureOptionsLoader(args),
    costPath: false,
  });
}

async function computeAutoSelectedTotal(
  args: ConfiguredInvestmentArgs,
  mode: "card" | "recommended" | "configured",
  options?: {
    includeTooling?: boolean;
    loadResponse?: ConfigureOptionsLoader;
    costPath?: boolean;
  },
): Promise<string | undefined> {
  if (!args.applicationIds.length) return undefined;

  // CR-006: "configured" keeps card semantics (single-machine total and MDS
  // parity special-cases) but applies the user's saved Configure edits against
  // the true regional catalog. With no edits its regional delta is zero.
  const isCardLike = mode !== "recommended";
  const seedFromStored = mode !== "card";

  try {
    const loadResponse = options?.loadResponse ?? configureOptionsLoader(args);
    const response = await loadResponse(options?.costPath ?? isCardLike);
    if (!response.available) return undefined;

    const family = response.meta.configureFamily;
    if (!family) return undefined;

    // POC parity (ground truth captured from running POC:3010, 2026-06-09):
    // among MDS products ONLY A240 sends a card override. For A230 (and any other
    // non-A240 MDS press) POC's `computeConfiguredMachinePriceForApps` returns 0 —
    // its card catalog exposes no auto-selectable MACHINE row — so POC sends NO
    // `machinePriceOverride` and the cost engine resolves the price server-side via
    // the `mds_a240_base_plus_robot` resolver (cheapest A240 base machine + €180k =
    // €243,500 → machine €0.696 → €0.70). MVP previously auto-selected a press from
    // A230's OWN catalog (a slightly cheaper machine, €61.5k) and sent that as an
    // override, which produced the ≤2¢ "press-pick" drift vs POC. Returning undefined
    // here routes A230 through the SAME server-side resolver POC uses, so both price
    // off the identical A240 base-machine pool and match exactly.
    // "More logical": pricing an A230 from A230's own catalog is arguably more
    // correct, but POC freezes the A240 pool for both — we copy POC for parity.
    // See parity proposals §0.6 / §3.
    if (
      isCardLike &&
      family === "mds" &&
      mdsProductKey(args.productId) !== "A240"
    ) {
      return undefined;
    }

    // The cost card remains a fresh POC-style recommendation. The display total
    // mirrors Configure Solution hydration: current family recommendations are
    // computed first, then explicit user overrides win. Untouched managed rows
    // may still refresh for the current assignment.
    const stored = seedFromStored ? loadProductSelection(args.productId) : null;
    const profilElementType =
      stored?.profilElementType ??
      response.meta.profilElementTypeDefault ??
      null;
    const mdsFilters: MdsFilterState = {
      fastenerGroup:
        stored?.mdsFastenerGroup ??
        response.meta.mdsFilters?.fastenerGroup ??
        null,
      feederTracks:
        stored?.mdsFeederTracks ??
        response.meta.mdsFilters?.feederTracks ??
        null,
      forceKn: stored?.mdsForceKn ?? response.meta.mdsFilters?.forceKn ?? null,
    };

    // POC parity (ground truth captured from running POC:3010, 2026-06-09): the
    // comparison-card machine price is ALWAYS the SINGLE-machine price (qty = 1); the
    // per-part `requiredMachines` multiplier is applied ONCE by the cost engine
    // (`cost_aggregateResults` scales machineCostPerPartDedicated by requiredMachines).
    // POC's auto-selects do write `× requiredMachines`, but in the card-override
    // context POC computes the override BEFORE the cost result exists (chicken-and-egg
    // inside `ensureCostCalc`), so `requiredMachines` defaults to 1 there — the
    // captured POC overrides are all qty=1:
    // • Heger 824 eDrive = €87,463 (base×1 + INSTALL1)
    // • T-Series = €288,323.77 (PAT-2400×1 + commissioning + support + jig
    // + MAS-350 + tooling) → engine ×req → machine €0.82 = POC
    // (NOT €1,009,651 — an earlier probe baked req and was wrong)
    // • A240 = €180,000 (robot adder only; handled below)
    // MVP used to send base×requiredMachines, so the card swung 2–16× with the user's
    // assumptions (the "changing assumptions changes every number" bug). Hence qty = 1
    // for every family — this reproduces POC exactly. See parity proposals §0.6.
    const selectionRequiredMachines = isCardLike
      ? 1
      : Math.max(1, Math.ceil(args.requiredMachines || 1));

    const recommended = applyAutoSelect({
      response,
      family,
      mspAutoFeedEnabled: args.mspAutoFeedEnabled,
      productId: args.productId,
      region: args.region,
      requiredMachines: selectionRequiredMachines,
      mdsFilters,
      profilElementType,
      existing: new Map(),
    }).selection;
    const selectablePartNos = configureSelectablePartNos({
      response,
      family,
      profilElementType,
      mdsFilters,
    });
    const selection = stored?.selectionFrozen
      ? new Map(
          [...itemsToSelectionMap(stored.items)].filter(([partNo]) =>
            selectablePartNos.has(partNo),
          ),
        )
      : applyUserSelectionOverrides(
          recommended,
          overridesToSelectionMap(stored?.userOverrides),
          selectablePartNos,
        );

    // POC parity (running POC, verified 2026-06-08/09): the A240 comparison card
    // prices the robot-integration adder ALONE (€180k). POC L11082 adds the adder
    // ONLY for A240, and A240's MDS catalog has no MACHINE row, so its FE override =
    // adder only. A230 (and any other MDS press) carries press × req with NO adder.
    // Here we drop any MACHINE selection for A240 so its override = adder only,
    // matching POC's A240 card (engine ×req → €0.51 at req=4). Modal/quote selection
    // is untouched (this only shapes the card override total).
    if (
      isCardLike &&
      family === "mds" &&
      mdsProductKey(args.productId) === "A240"
    ) {
      const machinePartNos = new Set(
        catalogItemsForFamily(response.catalogItems, "mds")
          .filter((i) => normUpper(i.subCategory) === "MACHINE")
          .map((i) => i.partNo),
      );
      for (const partNo of machinePartNos) selection.delete(partNo);
    }

    // svc-10 applies `requiredMachines` to the dedicated card result. A saved
    // machine-row quantity therefore identifies the chosen base machine; it is
    // not another multiplier in this card-like subtotal. Preserve explicit
    // deselection/identity, but price every selected base MACHINE exactly once.
    if (isCardLike) {
      for (const item of catalogItemsForFamily(response.catalogItems, family)) {
        if (
          normUpper(item.subCategory) === "MACHINE" &&
          selection.has(item.partNo)
        ) {
          selection.set(item.partNo, 1);
        }
      }
      // MICS-187: installation day and auto-feed upgrade scale per machine too.
      if (family === "heger_pem") {
        for (const partNo of perMachinePartNos(response)) {
          if (selection.has(partNo)) selection.set(partNo, 1);
        }
      }
    }

    const total = configuredOverrideTotal({
      selection,
      response,
      family,
      profilElementType,
      mdsFilters,
      extraAdder: familyAdder(family, response, args.productId),
      includeTooling: options?.includeTooling ?? mode === "recommended",
    });
    // A valid recommended configuration may have every priced option
    // explicitly deselected. Preserve that authoritative zero so the footer and
    // Optimize still retain labour/quality costs (§D.5). Card overrides remain
    // strictly positive because zero means "no override" to the cost engine.
    if (mode === "recommended" && total === 0) return "0.0000";
    return total > 0 ? total.toFixed(4) : undefined;
  } catch (error) {
    const rateLimitError = toCalculationRateLimitError(error);
    if (rateLimitError) throw rateLimitError;
    if (isRequestAbortError(error)) throw error;
    return undefined;
  }
}

/**
 * Family-specific adder folded into the configured sum so the override mirrors
 * the POC popup total. POC adds the MDS robot-integration adder ONLY for A240
 * (: `getMdsProductKey(product) === "A240"`), never for A230
 * or any other MDS press. The value is sourced from `meta.additionalInvestment`
 * (cost_models.additional_investment) exposed by svc-04 — never hardcoded.
 * See parity proposals §3.
 */
function familyAdder(
  family: ConfigureFamily,
  response: ConfigureOptionsResponse,
  productId: string,
): number | undefined {
  if (family !== "mds") return undefined;
  if (mdsProductKey(productId) !== "A240") return undefined;
  const raw = response.meta.additionalInvestment;
  const n = parseDecimalString(raw ?? null);
  return n != null && n > 0 ? n : undefined;
}
